import React from 'react';
import { ArcStack, ArcToolbar } from '@src/core/ui';
import { useDispatch, useSelector } from 'react-redux';
import { selectDrone, selectTerrainMode, selectWaypoints, setSidebarType, useCreateMissionMutation } from '@src/features/order/pages/upsert-mission-page/redux';
import { TerrainMode } from '@src/features/order/pages/upsert-mission-page/redux/extra-store/types';
import { IRouteForm, SidebarType } from '@src/features/order/pages/upsert-mission-page/entities';
import { useForm } from 'react-hook-form';
import { EMPTY_VALUE } from '@src/core/config';
import { SettingUnitType, useGetSettingByUnitType } from '@src/core/features';
import { EnterRouteForm } from '@src/features/order/pages/upsert-mission-page/components';
import { OrderIdParams } from '@src/core/entities';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { routesConfig } from '@src/core/routes';
import { useCancelOrderMutation } from '@src/features/order/redux';
import { CreateRouteSidebarProps } from './types';
import { arcToFixed } from '@src/core/extensions';

const CreateRouteSidebar: React.FC<CreateRouteSidebarProps> = (props) => {
  const { orderId, onSubmit: onSubmitProps, onCancel: onCancelProps } = props;
  const params = useParams<OrderIdParams>();
  const [queryParams] = useSearchParams();
  const effectiveOrderId = orderId || params.orderId;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const droneSerial = queryParams.get('droneSerial');
    
  const wps = useSelector(selectWaypoints);
  const selectedDrone = useSelector(selectDrone);
  const terrainMode = useSelector(selectTerrainMode);
  const isAdvanced = terrainMode === TerrainMode.ADVANCED;
  const speed = useGetSettingByUnitType(SettingUnitType.DELIVERY_SPEED);
  const deliveyMode = useGetSettingByUnitType(SettingUnitType.DELIVERY_MODE);
  const flightAltitude = useGetSettingByUnitType(SettingUnitType.FLIGHT_ALTITUDE);
  const deliveryAltitude = useGetSettingByUnitType(SettingUnitType.DELIVERY_ALTITUDE);

  const [cancelOrder, { isLoading: isCanceling }] = useCancelOrderMutation();
  const [createMission, { isLoading: isSubmitting }] = useCreateMissionMutation();


  const goBack = () => {
    // If user came from position-overlay (has droneSerial), navigate back to previous page
    if (droneSerial) {
      navigate(-1);
      return;
    }
    dispatch(setSidebarType(SidebarType.SELECT_DRONE));
  };

  const onCancel = () => {
    if (onCancelProps) {
      onCancelProps();
      return;
    }
    cancelOrder(effectiveOrderId).unwrap()
      .then(() => {
        toast.success('Order has been canceled');
        navigate(routesConfig.orders, { replace: true });
      })
      .catch(() => {});
  };

  const onSubmit = (form: IRouteForm) => {
    if (onSubmitProps) {
      onSubmitProps(form);
      return;
    }

    if (!selectedDrone) return;

    createMission({
      orderId: effectiveOrderId,
      droneId: selectedDrone.id,
      deliveryMode: form.deliveryMode,
      waypoints: wps.map((w) => ({
        orderId: w.orderId,
        altitude: w.altitude,
        latitude: w.latitude,
        longitude: w.longitude,
        speed: form.speed,
        deliveryAltitude: form.deliveryAltitude,
        ...(isAdvanced && { winchAltitude: form.winchAltitude }),
      })),
    }).unwrap().then((response) => {
      toast.success('Mission has been created');
      navigate(`${routesConfig.orders}/confirm-order/${response.missionId}?droneSerial=${response.drone.serialNumber}`);
    }).catch(() => {});
  };

  const form = useForm<IRouteForm>({ 
    defaultValues: {
      waypoints: [],
      speed: EMPTY_VALUE,
      deliveryMode: EMPTY_VALUE,
      takeOffAltitude: EMPTY_VALUE,
      deliveryAltitude: EMPTY_VALUE,
      winchAltitude: EMPTY_VALUE,
      destinationAltitude: EMPTY_VALUE,
    } 
  });

  React.useEffect(() => {
    form.setValue('speed', arcToFixed(speed.value));
    form.setValue('deliveryMode', deliveyMode.value);
    form.setValue('takeOffAltitude', arcToFixed(flightAltitude.value));
    form.setValue('deliveryAltitude', arcToFixed(deliveryAltitude.value));
    form.setValue('winchAltitude', arcToFixed(deliveryAltitude.value));
    form.setValue('destinationAltitude', arcToFixed(flightAltitude.value));
  }, [speed, deliveyMode, deliveryAltitude, flightAltitude]);

  return (
    <ArcStack spacing={2} height='100%'>
      <ArcToolbar 
        title='3. Enter route parameters' 
        goBack={goBack} />
      <EnterRouteForm 
        form={form} 
        goBack={onCancel} 
        onSubmit={onSubmit} 
        isCanceling={isCanceling}
        isSubmitting={isSubmitting} />
    </ArcStack>
  );
};

export default CreateRouteSidebar;